/**
 * The top bar: the Corteva lockup, the product name, the four tabs, and the
 * account controls.
 *
 * `View` and the tab table live here rather than in `App.tsx` because this is the
 * component that renders them; a tab added to the table but not to the switch is a
 * type error at the call site, which is where it should be.
 *
 * ── Two things here are not cosmetic ────────────────────────────────────────────
 *
 * **The name.** `ASTRA · Target Discovery` — the acronym in the display face, the
 * qualifier a step down beside it. The qualifier is not decoration and does not get
 * dropped: "target discovery" is the literal name of the AgentCore runtime, the CLI
 * (`target-discovery`), the hostname and the Azure AD group, so a reader who lands
 * here from any of those needs the two names in the same breath at least once. The
 * full expansion belongs on the sign-in gate and at the head of the Wiki, and
 * nowhere else — a header is not the place to explain an acronym on every page load.
 *
 * **The tabs are a real tablist.** They were `aria-pressed` toggle buttons, which
 * announce as independent switches; a screen-reader user got no indication
 * that picking one unpicked the others, and arrow keys did nothing. Now
 * `role="tablist"` / `role="tab"` / `aria-selected`, with a roving `tabIndex` so the
 * group is one Tab stop, and Left/Right/Home/End inside it.
 *
 * Activation is **manual**, not automatic: arrows move focus and Enter or Space
 * commits. The ARIA practices allow automatic activation only where showing a panel
 * is free, and it is not here — selecting *Board* is what enables `useBoard`, one
 * request over the whole cross-run history, so arrowing past it would fire a fetch
 * nobody asked for.
 *
 * What is still missing, and cannot be fixed from this file: `aria-controls` and the
 * matching `role="tabpanel"`. The panels mount in `App.tsx`, which is frozen, and
 * they carry no ids. The relationship is the one part of the pattern that needs both
 * ends.
 */
import { useRef, useState, type KeyboardEvent } from 'react'
import { LogOut, RefreshCw } from 'lucide-react'
import { Button } from './ui/Button'
import { Logo } from './Logo'
import { ThemeToggle } from './ThemeToggle'
import { cn } from '../lib/utils'

export type View = 'run' | 'board' | 'ask' | 'wiki'

const TABS: ReadonlyArray<readonly [View, string]> = [
  ['run', 'This run'],
  ['board', 'Board'],
  // Fourth in the type, third in the bar: it belongs beside the two records it
  // reads, not after the reference material. It fetches nothing on selection —
  // the first request is a question — so arrowing past it is free, unlike Board.
  ['ask', 'Ask'],
  ['wiki', 'Wiki'],
]

interface Props {
  view: View
  onViewChange: (view: View) => void
  onRefresh: () => void
  refreshing: boolean
  /**
   * The signed-in account's UPN, or null when auth is disabled or nobody is signed
   * in. Entra puts the UPN in `username`; `name` is the display name and
   * `localAccountId` is a uuid, useless as a label.
   */
  accountName: string | null
  /** Null when there is nobody to sign out, which hides the control entirely. */
  onSignOut: (() => void) | null
}

export function AppHeader({
  view,
  onViewChange,
  onRefresh,
  refreshing,
  accountName,
  onSignOut,
}: Props) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
  /**
   * Which tab holds the group's single Tab stop while the keyboard is inside it.
   *
   * Null means "wherever the selection is", which is the state the group must
   * return to once focus leaves — otherwise a reader who arrowed to *Wiki*, tabbed
   * away without committing and came back would land on a tab that is not the one
   * showing.
   */
  const [focusedTab, setFocusedTab] = useState<number | null>(null)
  const selectedTab = Math.max(
    0,
    TABS.findIndex(([key]) => key === view),
  )
  const rovingTab = focusedTab ?? selectedTab

  const moveFocus = (to: number) => {
    const next = (to + TABS.length) % TABS.length
    setFocusedTab(next)
    tabRefs.current[next]?.focus()
  }

  const onTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowLeft':
        moveFocus(rovingTab - 1)
        break
      case 'ArrowRight':
        moveFocus(rovingTab + 1)
        break
      case 'Home':
        moveFocus(0)
        break
      case 'End':
        moveFocus(TABS.length - 1)
        break
      default:
        // Everything else, Enter and Space included, is the button's own business.
        return
    }
    // Only for the four keys handled above: Left/Right otherwise scroll the page
    // and Home/End jump it to the ends.
    event.preventDefault()
  }

  return (
    // 56px, and `shrink-0` so a long run id in the account button cannot squeeze it.
    //
    // `sticky` is belt-and-braces rather than load-bearing: `App.tsx` scrolls the run
    // rail and the main column independently, so the bar is already outside both
    // scroll containers. It stays because the tabs are how a reader gets out of a
    // long board, and that must not depend on a layout decision in another file. The
    // translucent ground and `backdrop-blur` are what stop rows of mono accessions
    // reading through it if it ever does overlap them.
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-stretch gap-4 border-b border-slate-800 bg-slate-950/95 px-5 backdrop-blur">
      <div className="flex min-w-0 items-center gap-3">
        <Logo size={22} className="text-slate-100" />
        <div className="min-w-0">
          <h1 className="truncate font-display text-base font-bold leading-tight tracking-tight text-slate-100">
            ASTRA
            {/* One step down and muted, so the pair reads as a name and a
                qualifier rather than as a two-word name. */}
            <span className="text-sm font-medium text-slate-400">
              {' · '}Target Discovery
            </span>
          </h1>
          <p className="hidden truncate text-xs leading-tight text-slate-500 md:block">
            Ranked crop-protection target proteins
          </p>
        </div>
      </div>

      <span className="my-4 w-px shrink-0 bg-slate-800" aria-hidden="true" />

      {/* Full-height so the active tab's rule sits on the header's own bottom
          border rather than floating above it. */}
      <div
        role="tablist"
        aria-label="View"
        onKeyDown={onTabKeyDown}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setFocusedTab(null)
          }
        }}
        className="-mb-px flex shrink-0 items-stretch gap-1"
      >
        {TABS.map(([key, label], index) => (
          <button
            key={key}
            ref={(node) => {
              tabRefs.current[index] = node
            }}
            type="button"
            role="tab"
            aria-selected={view === key}
            tabIndex={index === rovingTab ? 0 : -1}
            onClick={() => {
              setFocusedTab(index)
              onViewChange(key)
            }}
            className={cn(
              'inline-flex items-center border-b-2 px-3 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
              view === key
                ? 'border-brand-500 text-slate-100'
                : 'border-transparent text-slate-400 hover:border-slate-700 hover:text-slate-200',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-2">
        <ThemeToggle />
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing}>
          <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
          Refresh
        </Button>
        {onSignOut && (
          <Button variant="ghost" size="sm" onClick={onSignOut}>
            <LogOut className="h-3.5 w-3.5" />
            {/* The UPN is an email address and can be long; truncated rather than
                allowed to push the tabs off the bar. */}
            <span className="max-w-[14rem] truncate">
              {accountName || 'Sign out'}
            </span>
          </Button>
        )}
      </div>
    </header>
  )
}
